import { useAuthStore } from '@/stores/auth'

/**
 * 用户专属 LocalStorage 工具类
 *
 * 存储格式: ct_{租户ID}_{用户ID}_{自定义key}
 *
 * @example
 * UserStorage.set('recently_used_ports', [1, 2, 3])
 * const data = UserStorage.get('recently_used_ports', [])
 * UserStorage.remove('recently_used_ports')
 * UserStorage.clear()
 */
export class UserStorage {
  private static readonly PREFIX = 'ct'

  /** 获取用户存储前缀 */
  private static getUserPrefix(): string {
    const user = useAuthStore().userInfo
    return user?.tenantId && user?.id
      ? `${this.PREFIX}_${user.tenantId}_${user.id}_`
      : `${this.PREFIX}_guest_`
  }

  /** 生成完整key */
  private static getFullKey(key: string): string {
    return `${this.getUserPrefix()}${key}`
  }

  /** 保存数据 */
  static set<T = any>(key: string, value: T): boolean {
    try {
      const fullKey = this.getFullKey(key)
      localStorage.setItem(fullKey, JSON.stringify(value))
      import.meta.env.DEV && console.log(`[UserStorage] 保存: ${fullKey}`)
      return true
    } catch (error) {
      console.error('[UserStorage] 保存失败:', error)
      return false
    }
  }

  /** 获取数据 */
  static get<T = any>(key: string, defaultValue: T): T {
    try {
      const data = localStorage.getItem(this.getFullKey(key))
      return data ? JSON.parse(data) : defaultValue
    } catch (error) {
      console.error('[UserStorage] 读取失败:', error)
      return defaultValue
    }
  }

  /** 移除数据 */
  static remove(key: string): boolean {
    try {
      localStorage.removeItem(this.getFullKey(key))
      return true
    } catch (error) {
      console.error('[UserStorage] 移除失败:', error)
      return false
    }
  }

  /** 检查是否存在 */
  static has(key: string): boolean {
    return localStorage.getItem(this.getFullKey(key)) !== null
  }

  /** 清除当前用户所有数据 */
  static clear(): number {
    const prefix = this.getUserPrefix()
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
      .filter(key => key?.startsWith(prefix))

    keys.forEach(key => key && localStorage.removeItem(key))
    import.meta.env.DEV && console.log(`[UserStorage] 清除 ${keys.length} 条数据`)
    return keys.length
  }

  /** 获取所有key列表 */
  static keys(): string[] {
    const prefix = this.getUserPrefix()
    return Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
      .filter(key => key?.startsWith(prefix))
      .map(key => key!.substring(prefix.length))
  }

  /** 获取存储使用情况 */
  static getUsage(): { bytes: number; count: number } {
    const prefix = this.getUserPrefix()
    let bytes = 0
    let count = 0

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(prefix)) {
        const value = localStorage.getItem(key)
        if (value) {
          bytes += (key.length + value.length) * 2
          count++
        }
      }
    }

    return { bytes, count }
  }

  /** 清除指定租户所有数据 */
  static clearTenant(tenantId: string): number {
    const prefix = `${this.PREFIX}_${tenantId}_`
    const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i))
      .filter(key => key?.startsWith(prefix))

    keys.forEach(key => key && localStorage.removeItem(key))
    import.meta.env.DEV && console.log(`[UserStorage] 清除租户 ${tenantId}: ${keys.length} 条`)
    return keys.length
  }
}

/**
 * 最近使用数据管理器
 *
 * @example
 * const manager = new RecentlyUsedManager('recently_used_ports', 50)
 * manager.add(123)
 * const list = manager.getList()
 * manager.has(123)
 * manager.clear()
 */
export class RecentlyUsedManager<T = any> {
  constructor(
    private key: string,
    private maxSize: number = 50
  ) {}

  /** 添加最近使用记录 */
  add(value: T): boolean {
    try {
      const list = this.getList().filter(item => item !== value)
      list.unshift(value)
      return UserStorage.set(this.key, list.slice(0, this.maxSize))
    } catch (error) {
      console.error('[RecentlyUsedManager] 添加失败:', error)
      return false
    }
  }

  /** 获取列表 */
  getList(): T[] {
    return UserStorage.get<T[]>(this.key, [])
  }

  /** 检查是否存在 */
  has(value: T): boolean {
    return this.getList().includes(value)
  }

  /** 移除指定值 */
  remove(value: T): boolean {
    return UserStorage.set(this.key, this.getList().filter(item => item !== value))
  }

  /** 清空列表 */
  clear(): boolean {
    return UserStorage.remove(this.key)
  }

  /** 获取数量 */
  getCount(): number {
    return this.getList().length
  }

  /** 获取前N个 */
  getTop(count: number): T[] {
    return this.getList().slice(0, count)
  }
}

